import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE, ACTIVE, INACTIVE, VIEW, EDIT,
} from "const"
import { mtModulesRoute } from "client/parts/routes"
import { MtModule, MtModuleCreate } from "server/models"
import {
    useInput,
    StateType,
    replaceHistoryState,
    forceRerenderContent,
} from "client/utils"
import {
    StoreType,
    ModuleValidation,
    SaveModule,
    closeModal,
    setModuleEdit,
    setModuleReloadList,
    setModuleReloadView,
    setModuleReloadEdit,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    CKEditor,
    Feedback,
    SelectDropdown,
    SelectDropdownItem,
    FileLoader,
    Button,
    SpinnerIcon,
} from "client/components"

interface EditModuleProps {
    module: MtModule
}

let isMounted: boolean

const Form = ({
    module,
}: EditModuleProps) => {
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(module.name)
    const [shortName, setShortName] = useInput(module.shortName)

    const [description, setDescription] = useState(module.description)
    const [copyright, setCopyright] = useState(module.copyright)
    const [status, setStatus] = useState(module.status)
    const [logoId, setLogoId] = useState(module.logoId)
    const [logo, setLogo] = useState(module.logo)

    const [validation, setValidation] = useState<ModuleValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        const path = module.id
            ? `${mtModulesRoute}/${VIEW}/${module.id}`
            : mtModulesRoute

        forceRerenderContent(path, dispatch)
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newModuleData: MtModuleCreate = {
            name,
            shortName,
            description,
            logoId,
            copyright,
            status,
        }

        SaveModule(module.id, newModuleData, lang)
        .then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)

                if (res.newModuleId) replaceHistoryState(`${mtModulesRoute}/${EDIT}/${res.newModuleId}`)
            }
            if (res.validation) return

            dispatch(setModuleEdit({
                id: res.newModuleId || module.id,
                logo,
                ...newModuleData,
            }))

            dispatch(setModuleReloadList(false))
            dispatch(setModuleReloadView(false))
            setTimeout(() => {
                dispatch(setModuleReloadList(true))
                dispatch(setModuleReloadView(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setModuleReloadEdit(false))
                setTimeout(() => {
                    dispatch(setModuleReloadEdit(true))
                }, 0)
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12} md={8}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={12} md={4}>
                    <FormGroup>
                        <Label>{lang.shortName}</Label>
                        <Input type="text" value={shortName} onChange={setShortName} />
                        <Feedback>{validation && validation.shortName}</Feedback>
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.image}</Label>
                        <FileLoader
                            value={logo}
                            callback={(file) => {
                                setLogoId(file.id)
                                setLogo(file)
                            }}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.description}</Label>
                        <CKEditor
                            data={description}
                            onChange={setDescription}
                        />
                        <Feedback>{validation && validation.description}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.copyright}</Label>
                        <CKEditor
                            data={copyright}
                            onChange={setCopyright}
                        />
                        <Feedback>{validation && validation.copyright}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.status}</Label>
                        <SelectDropdown title={lang[status] || ""}>
                            <SelectDropdownItem onClick={() => setStatus(ACTIVE)}>{lang[ACTIVE]}</SelectDropdownItem>
                            <SelectDropdownItem onClick={() => setStatus(INACTIVE)}>{lang[INACTIVE]}</SelectDropdownItem>
                        </SelectDropdown>
                        <Feedback>{validation && validation.status}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
